/* LMC Product video — YouTube facade with muted autoplay-in-view.

   <lmc-youtube data-yt-id="..."> renders a poster + play button. It
   swaps in the YouTube iframe (privacy domain, rel=0 & modestbranding
   to minimise YouTube's own chrome) when EITHER:
   - it scrolls into view → autoplay MUTED (browsers block autoplay
     with sound; the visitor unmutes via the player controls), or
   - the visitor clicks the poster → play with sound.

   Deferring the iframe until one of those keeps the PDP fast and shows
   no YouTube branding until playback starts. Autoplay is muted, which
   is the standard treatment for an auto-starting product video. */

(function () {
  'use strict';

  // Bail on browsers without Custom Elements support (otherwise the
  // customElements.define call below throws), and don't redefine.
  if (!window.customElements) return;
  if (customElements.get('lmc-youtube')) return;

  class LMCYouTube extends HTMLElement {
    connectedCallback() {
      // The poster is the element's only button; query by tag so this
      // works regardless of the host section's class names.
      const trigger = this.querySelector('button');
      if (!trigger) return;
      // Click = play with sound (a user gesture, so unmuted is allowed).
      trigger.addEventListener('click', () => this.play({ muted: false }), { once: true });

      // Autoplay (muted) once the video scrolls into view.
      // IntersectionObserver guards browsers that lack it (they fall
      // back to click-to-play).
      if (!('IntersectionObserver' in window)) return;
      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.play({ muted: true });
          }
        },
        { threshold: 0.4 }
      );
      this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    play(options) {
      const id = this.getAttribute('data-yt-id');
      if (!id || this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      if (this._observer) this._observer.disconnect();

      const muted = options && options.muted;
      const params = new URLSearchParams({
        autoplay: '1',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
      });
      // Autoplay without a user gesture only works muted.
      if (muted) params.set('mute', '1');

      const iframe = document.createElement('iframe');
      // Encode the id defensively — it's merchant-entered via a metafield.
      iframe.src =
        'https://www.youtube-nocookie.com/embed/' +
        encodeURIComponent(id) +
        '?' +
        params.toString();
      iframe.title = 'Product video';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.loading = 'eager';

      this.innerHTML = '';
      this.appendChild(iframe);
    }
  }

  customElements.define('lmc-youtube', LMCYouTube);
})();
